<template>
  <DetailsSidebar
    v-model:visible="visible"
    title="Alert Details"
    width="560px"
    test-id="alert-details-sidebar"
  >
    <div v-if="alert" class="h-full flex flex-col min-h-0 overflow-y-auto">
      <!-- Region 1: Chart -->
      <section
        class="p-4 border-b border-surface-200 dark:border-surface-700"
        data-test-id="alert-details-chart"
      >
        <template v-if="chartSpec">
          <div class="aspect-square w-full">
            <AnalyticsChart
              :spec="chartSpec"
              :merchant-id="String(alert.MERCHANT_ID)"
              :date-from="chartDateFrom"
              :date-to="chartDateTo"
              :pinnable="false"
            />
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center justify-center py-6 text-center">
            <p class="text-sm text-surface-500 dark:text-surface-400">
              No chart available for this event type — see metadata below.
            </p>
          </div>
        </template>
      </section>

      <!-- Region 2: Metadata -->
      <section
        class="p-4"
        data-test-id="alert-details-metadata"
      >
        <h3 class="text-sm font-semibold text-surface-700 dark:text-surface-200 uppercase tracking-wide mb-3">
          Alert Details
        </h3>
        <dl class="grid grid-cols-2 gap-x-4 gap-y-3">
          <div data-test-id="alert-meta-event-type">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Event Type
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ formatEventType(alert.EVENT_TYPE) }}
            </dd>
          </div>
          <div data-test-id="alert-meta-threshold">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Threshold
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ formatAlertType(alert.THRESHOLD, alert.EVENT_TYPE) }}
            </dd>
          </div>
          <div data-test-id="alert-meta-observed">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Observed
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ formatAlertType(alert.STATISTIC, alert.EVENT_TYPE) }}
            </dd>
          </div>
          <div data-test-id="alert-meta-tolerance">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Tolerance
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ formatTolerance(alert.TOLERANCE) }}
            </dd>
          </div>
          <div data-test-id="alert-meta-significance">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Significance
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ formatSignificance(alert.SIGNIFICANCE) }}
            </dd>
          </div>
          <div data-test-id="alert-meta-alert-age">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Alert Age
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              {{ alertAge }}
            </dd>
          </div>
          <div class="col-span-2" data-test-id="alert-meta-supplier">
            <dt class="text-xs text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-1">
              Supplier
            </dt>
            <dd class="text-sm font-medium text-surface-900 dark:text-surface-0">
              <RouterLink
                :to="{ name: 'fccMerchantDetails', params: { id: alert.MERCHANT_ID } }"
                class="hover:underline"
              >
                {{ alert.MERCHANT_NAME || `Supplier #${alert.MERCHANT_ID}` }}
                ({{ alert.MERCHANT_ID }})
              </RouterLink>
            </dd>
          </div>
        </dl>
      </section>
    </div>
  </DetailsSidebar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { Alert } from '@/types/fcc'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import DetailsSidebar from '@/components/fcc/DetailsSidebar.vue'
import AnalyticsChart from '@/components/fcc/charts/AnalyticsChart.vue'
import { getAnalyticsKind } from '@/util/fcc/eventTypeToAnalyticsKind'
import { CHART_SPECS } from '@/util/fcc/charts/chartSpecs'
import { defaultDateRange } from '@/util/fcc/charts/dateRange'

interface Props {
  visible: boolean
  alert: Alert | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const { formatEventType, formatAlertType, formatSignificance, formatTolerance } =
  useFCCFormatting()

const visible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
})

const alertKind = computed(() =>
  props.alert ? getAnalyticsKind(props.alert.EVENT_TYPE) : null,
)

const chartSpec = computed(() => {
  if (!alertKind.value) return null
  return CHART_SPECS.find(s => s.kind === alertKind.value) ?? null
})

const chartRange = defaultDateRange(90)
const chartDateFrom = chartRange.date_from
const chartDateTo = chartRange.date_to

const alertAge = computed((): string => {
  const raw = props.alert?.TIMESTAMP || props.alert?.EVENT_DATE
  if (!raw) return '-'
  const now = Date.now()
  const ts = new Date(raw).getTime()
  const diffMs = now - ts
  if (diffMs < 0) return '-'
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    return diffHours <= 1 ? 'Less than 1 hour ago' : `${diffHours} hours ago`
  }
  return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
})
</script>
